import { type StructuredContentDocument } from '@repo/contracts/multi-use/structured-content/types';
import { css } from '@repo/pandacss/styled-system/css';
import { MdCheck, MdClose } from 'react-icons/md';
import { StructuredContentRenderer } from '../StructuredContentRenderer';

type Props = {
  prosList?: string[] | StructuredContentDocument[];
  consList?: string[] | StructuredContentDocument[];
};

export function ColoredBulletsProsAndCons({ prosList, consList }: Props) {
  const hasPros = !!prosList?.length;
  const hasCons = !!consList?.length;

  if (!hasPros && !hasCons) return null;

  return (
    <div
      data-testid="colored-bullets-pros-and-cons"
      className={css({
        display: 'flex',
        flexDirection: { base: 'column', sm: 'row' },
        columnGap: '6',
        rowGap: '6',
      })}
    >
      {hasPros && (
        <div
          className={css({
            flexGrow: '[1]',
            flexShrink: '[1]',
            flexBasis: '[0%]',
            borderTopColor: 'neutral.lighter',
            borderTopWidth: 'thin',
            borderTopStyle: 'solid',
            borderTopLeftRadius: 'md',
            borderTopRightRadius: 'md',
            borderBottomLeftRadius: 'md',
            borderBottomRightRadius: 'md',
            borderBottomColor: 'neutral.lighter',
            borderBottomWidth: 'thin',
            borderBottomStyle: 'solid',
            borderLeftColor: 'neutral.lighter',
            borderLeftWidth: 'thin',
            borderLeftStyle: 'solid',
            borderRightColor: 'neutral.lighter',
            borderRightWidth: 'thin',
            borderRightStyle: 'solid',
            paddingBlockStart: '4',
            paddingBlockEnd: '4',
            paddingInlineStart: '4',
            paddingInlineEnd: '4',
          })}
        >
          <p
            className={css({
              textStyle: 'textBold',
              marginBottom: '1',
              display: 'block',
              color: 'black',
              fontWeight: 'bold',
            })}
          >
            Pros
          </p>
          <ul
            className={css({
              listStyleType: 'disc',
              paddingTop: '0',
              paddingRight: '0',
              paddingBottom: '0',
              paddingLeft: '0',
            })}
          >
            {prosList.map((bullet, index) => (
              <li
                key={`pro-${index}`}
                className={css({
                  paddingBlockStart: '1',
                  paddingBlockEnd: '1',
                  paddingInlineStart: '0',
                  paddingInlineEnd: '0',
                  display: 'flex',
                  alignItems: 'flex-start',
                  columnGap: '1',
                  rowGap: '1',
                })}
              >
                <span
                  className={css({
                    display: 'grid',
                    alignItems: 'center',
                    justifyItems: 'center',
                    borderTopLeftRadius: 'half',
                    borderTopRightRadius: 'half',
                    borderBottomRightRadius: 'half',
                    borderBottomLeftRadius: 'half',
                    marginTop: '1',
                    backgroundColor: 'green.lightest',
                    minWidth: '4',
                    minHeight: '4',
                    flexShrink: '0',
                  })}
                  aria-hidden="true"
                >
                  <MdCheck className={css({ color: 'green.main', width: '3.5', height: '3.5' })} />
                </span>
                {typeof bullet === 'string' ? (
                  <span>{bullet}</span>
                ) : (
                  <StructuredContentRenderer doc={bullet} />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasCons && (
        <div
          className={css({
            flexGrow: '[1]',
            flexShrink: '[1]',
            flexBasis: '[0%]',
            borderTopColor: 'neutral.lighter',
            borderTopWidth: 'thin',
            borderTopStyle: 'solid',
            borderTopLeftRadius: 'md',
            borderTopRightRadius: 'md',
            borderBottomLeftRadius: 'md',
            borderBottomRightRadius: 'md',
            borderBottomColor: 'neutral.lighter',
            borderBottomWidth: 'thin',
            borderBottomStyle: 'solid',
            borderLeftColor: 'neutral.lighter',
            borderLeftWidth: 'thin',
            borderLeftStyle: 'solid',
            borderRightColor: 'neutral.lighter',
            borderRightWidth: 'thin',
            borderRightStyle: 'solid',
            paddingBlockStart: '4',
            paddingBlockEnd: '4',
            paddingInlineStart: '4',
            paddingInlineEnd: '4',
          })}
        >
          <p
            className={css({
              textStyle: 'textBold',
              marginBottom: '1',
              display: 'block',
              color: 'black',
              fontWeight: 'bold',
            })}
          >
            Cons
          </p>
          <ul
            className={css({
              listStyleType: 'disc',
              paddingTop: '0',
              paddingRight: '0',
              paddingBottom: '0',
              paddingLeft: '0',
            })}
          >
            {consList.map((bullet, index) => (
              <li
                key={`con-${index}`}
                className={css({
                  paddingBlockStart: '1',
                  paddingBlockEnd: '1',
                  paddingInlineStart: '0',
                  paddingInlineEnd: '0',
                  display: 'flex',
                  alignItems: 'flex-start',
                  columnGap: '1',
                  rowGap: '1',
                })}
              >
                <span
                  className={css({
                    display: 'grid',
                    alignItems: 'center',
                    justifyItems: 'center',
                    borderTopLeftRadius: 'half',
                    borderTopRightRadius: 'half',
                    borderBottomRightRadius: 'half',
                    borderBottomLeftRadius: 'half',
                    marginTop: '1',
                    backgroundColor: 'red.lightest',
                    minWidth: '4',
                    minHeight: '4',
                    flexShrink: '0',
                  })}
                  aria-hidden="true"
                >
                  <MdClose className={css({ color: 'red.main', width: '3.5', height: '3.5' })} />
                </span>
                {typeof bullet === 'string' ? bullet : <StructuredContentRenderer doc={bullet} />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
